import "./styles.scss";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  putFillForm,
  getAvailableTopics,
  putUpdateFilledForm,
  getNextContentsOfStudent,
  getCurrentCycleAndLevelByStudentId,
} from "../../store/middleware";
import Tabs from "../../components/Tabs";
import Header from "../../components/Header/NewHeader";
import SideMenu from "../../components/SideMenu/SideMenu";
import CardReport from "../../components/report/CardReport";
import ButtonBack from "../../components/report/ButtonBack";
import iconReportSVG from "../../assets/images/icons/report.svg";
import CardClassesHeld from "../../components/report/CardClassesHeld";
import HeaderClassesHeal from "../../components/report/HeaderClassesHeal";
import CardLevelAndCycle from "../../components/report/CardLevelAndCycle";
import CommentsClassesHeld from "../../components/report/CommentsClassesHeld";
import { setNextContentsStudent } from "../../store/slices/methodology.slice";
import ModalSelectLevelStudent from "../../components/report/ModalSelectLevelStudent";
import ModalAlertContentSpecific from "../../components/report/ModalAlertContentSpecific";
import ModalCommunicationsCoordinator from "../../components/report/ModalCommunicationsCoordinator";

const ReportDetails = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { report, nextContentsStudent, currentCycleAndLevelStudent } = useSelector((state) => state.methodologyReducer);

  const [commentsTeacher, setCommentsTeacher] = useState("");
  const [contentSelected, setContentSelected] = useState([]);
  const [activeTab, setActiveTab] = useState("CLASSES_HELD");
  const [showModalCoordinator, setShowModalCoordinator] = useState(false);
  const [suggestedExercisesOptions, setSuggestedExercisesOptions] = useState([]);
  const [suggestedExercisesByTeacher, setSuggestedExercisesByTeacher] = useState([]);
  const [showModalAddContentSpecific, setShowModalAddContentSpecific] = useState(false);
  const [showModalSelectLevelStudent, setShowModalSelectLevelStudent] = useState(false);

  const options = ["CLASSES_HELD", "CLASS_COMMENTARY"];
  const hasStudentCycleAndLevel = currentCycleAndLevelStudent.length === 0;
  const statusDoneReport = report[0]?.status === "done" || report[0]?.status === "done-delayed";

  const openModalSelectLevelStudent = () => setShowModalSelectLevelStudent(true);

  const validateForm = () => {
    if (contentSelected.length === 0) {
      alert(t("INVALID_FORM_DESCRIPTION"));
      return false;
    }
    if (!commentsTeacher) {
      alert(t("INVALID_FORM_DESCRIPTION"));
      return false;
    }
    if (commentsTeacher.length < 20) {
      alert("Minimum number of 20 characters");
      return false;
    }
    if (hasStudentCycleAndLevel) {
      alert(t("Fill in the student level"));
      return false;
    }

    return true;
  };

  const handleNavigationNextStep = () => {
    if (contentSelected.length === 0) {
      alert(t("INVALID_FORM_DESCRIPTION"));
      return;
    }

    const contentSelectedFilter = nextContentsStudent.filter((item, i) => item.id === contentSelected[i]);
    const pageAndCommentRequired = contentSelectedFilter.filter((item) => {
      if (item.classNotCompleted) return !(item?.pageFinished?.trim() && item?.continueComment?.trim());
      return false;
    });

    if (pageAndCommentRequired.length > 0) {
      alert(t("PAGE_COMMENT_REQUIRED"));
      return;
    }

    setActiveTab("CLASS_COMMENTARY");
  };

  const openShowModalCoordinator = () => {
    if (!validateForm()) return false;
    setShowModalCoordinator(true);
  };

  const getData = async () => {
    const payload = {
      limit: 10,
      studentId: report[0]?.studentId,
      change: statusDoneReport ? true : false,
      lingoLanguageId: report[0]?.lingoLanguageId,
      sequentialScheduleId: report[0]?.sequentialScheduleId,
    };

    await dispatch(getNextContentsOfStudent(payload));

    const response = await dispatch(getAvailableTopics(report[0]?.lingoLanguageId));
    setSuggestedExercisesOptions(response);
  };

  const getCycleLevel = () => {
    const payload = {
      studentId: report[0]?.studentId,
      language: report[0]?.lingoLanguageId,
    };

    dispatch(getCurrentCycleAndLevelByStudentId(payload));
  };

  const handleButtonContentSpecific = () => {
    if (nextContentsStudent?.some((item) => item.focus?.toLowerCase() === "specific")) return;
    setShowModalAddContentSpecific(true);
  };

  const handleConfirmContentSpecific = () => {
    const payload = {
      id: 999,
      checkbox: true,
      pageFinished: "",
      focus: "Specific",
      continueComment: "",
      classFocus: "Specific",
      specificClassTeacherComment: "",
      suggestedExercisesByTeacher: "",
      sequentialScheduleId: report[0]?.sequentialScheduleId,
    };

    setContentSelected((state) => [...state, 999]);
    dispatch(setNextContentsStudent([payload, ...nextContentsStudent]));
  };

  const onSubmit = async () => {
    if (!validateForm()) return;

    const contentSelectedFilter = nextContentsStudent.filter((item) => contentSelected.includes(item?.id));

    const payload = contentSelectedFilter
      .map((item) => ({
        commentForCordinator: "",
        studentStatusByTeacher: "",
        contentCommentByStudent: "",
        classFocus: item?.classFocus || "",

        contentId: item.contentid,
        pageFinished: item.pageFinished,
        continueComment: item?.continueComment,
        studentCommentByTeacher: commentsTeacher,
        sequentialScheduleId: report[0]?.sequentialScheduleId,
        suggestedExercisesByTeacher: suggestedExercisesByTeacher?.length
          ? suggestedExercisesByTeacher.join(", ")
          : null,
      }))
      .filter((item) => Object.keys(item).length > 0);

    if (statusDoneReport) {
      await dispatch(putUpdateFilledForm(payload)).catch((error) => alert(error.response.data.error));
    } else {
      await dispatch(putFillForm(payload)).catch((error) => alert(error.response.data.error));
    }

    getData();
  };

  useEffect(() => {
    if (statusDoneReport) {
      const idSelected = nextContentsStudent.filter((item) => item.contentExists).map((item) => item.id);

      setCommentsTeacher(report[0]?.studentCommentByTeacher);
      setContentSelected((state) => [...new Set([...state, ...idSelected])]);
    }

    // eslint-disable-next-line
  }, [nextContentsStudent]);

  useEffect(() => {
    getData();
    getCycleLevel();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="view">
      <SideMenu />

      <section>
        <Header
          typeIcon="custom"
          title={t("REPORT_FORM")}
          icon={<img src={iconReportSVG} alt="ReportDetails Icon" />}
        />

        <div className="content">
          <CardReport detail={true} data={report} />

          <div className="wrapperTabs">
            <Tabs options={options} activeTab={activeTab} setActiveTab={setActiveTab}>
              <ButtonBack />

              <HeaderClassesHeal
                activeTab={activeTab}
                onSubmitReport={onSubmit}
                openShowModalCoordinator={openShowModalCoordinator}
                handleNavigationNextStep={handleNavigationNextStep}
                handleButtonContentSpecific={handleButtonContentSpecific}
              />

              <CardLevelAndCycle openModalSelectLevelStudent={openModalSelectLevelStudent} />

              {!hasStudentCycleAndLevel && (
                <>
                  {options[0] === activeTab && (
                    <CardClassesHeld contentSelected={contentSelected} setContentSelected={setContentSelected} />
                  )}
                  {options[1] === activeTab && (
                    <CommentsClassesHeld
                      commentsTeacher={commentsTeacher}
                      setCommentsTeacher={setCommentsTeacher}
                      suggestedExercisesOptions={suggestedExercisesOptions}
                      suggestedExercisesByTeacher={suggestedExercisesByTeacher}
                      setSuggestedExercisesByTeacher={setSuggestedExercisesByTeacher}
                    />
                  )}
                </>
              )}
            </Tabs>
          </div>
        </div>

        <ModalSelectLevelStudent
          showModal={showModalSelectLevelStudent}
          setShowModal={setShowModalSelectLevelStudent}
        />

        <ModalAlertContentSpecific
          showModal={showModalAddContentSpecific}
          setShowModal={setShowModalAddContentSpecific}
          handleConfirmContentSpecific={handleConfirmContentSpecific}
        />

        <ModalCommunicationsCoordinator showModal={showModalCoordinator} setShowModal={setShowModalCoordinator} />
      </section>
    </div>
  );
};

export default ReportDetails;
